import React, { useState, useEffect } from 'react';
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { addDays, format, startOfToday, setHours, setMinutes, startOfDay } from "date-fns";
import { Clock, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { isDateDisabled, isTimeDisabled, getDisabledReason } from "@/lib/datetime-utils";
import { useTheme } from "@/hooks/use-theme";
interface CollectionDateTimeSelectorProps {
  collectionDateTime: string;
  onUpdate: (dateTime: string) => void;
  currentTestTime: Date;
}
const CollectionDateTimeSelector: React.FC<CollectionDateTimeSelectorProps> = ({
  collectionDateTime,
  onUpdate,
  currentTestTime
}) => {
  const today = startOfToday();
  const {
    primaryColor
  } = useTheme();

  // State to track selected date and time
  const [selectedDate, setSelectedDate] = useState<string | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState<string | undefined>("12");

  // Generate next 14 days for date selection
  const dateOptions = Array.from({
    length: 14
  }, (_, i) => {
    const date = addDays(today, i);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6; // Sunday = 0, Saturday = 6
    return {
      value: date.toISOString(),
      label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : format(date, 'EEE, MMM d'),
      date,
      disabled: isDateDisabled(date, currentTestTime),
      isWeekend
    };
  });

  // Generate time slots for time selection (9am to 6pm)
  const timeOptions = Array.from({
    length: 10
  }, (_, i) => {
    const hour = i + 9; // Starting from 9am
    const dateForTimeSlot = selectedDate ? startOfDay(new Date(selectedDate)) : today;
    return {
      value: hour.toString(),
      label: format(setHours(today, hour), 'h:mm a'),
      hour,
      disabled: selectedDate ? isTimeDisabled(dateForTimeSlot, hour, currentTestTime) : false
    };
  });

  // Initialize selectedDate after the variable declaration and dateOptions are ready
  useEffect(() => {
    // Set initial selected date
    if (!selectedDate) {
      if (collectionDateTime) {
        const existingDate = dateOptions.find(d => format(startOfDay(new Date(d.value)), 'yyyy-MM-dd') === format(startOfDay(new Date(collectionDateTime)), 'yyyy-MM-dd'));
        setSelectedDate(existingDate?.value || dateOptions.find(d => !d.disabled)?.value);
      } else {
        setSelectedDate(dateOptions.find(d => !d.disabled)?.value);
      }
    }

    // Set initial selected time
    if (collectionDateTime) {
      setSelectedTime(new Date(collectionDateTime).getHours().toString());
    }
  }, [collectionDateTime, dateOptions]);

  // Update collection datetime when date or time changes
  const updateCollectionDateTime = (date: string, time: string) => {
    const dateObj = startOfDay(new Date(date));
    const hour = parseInt(time);
    const newDateTime = setHours(setMinutes(dateObj, 0), hour);
    onUpdate(newDateTime.toISOString());
  };

  // Update selected date when date options change due to time changes
  useEffect(() => {
    // If the currently selected date is now disabled, select the first available date
    if (selectedDate) {
      const currentDateOption = dateOptions.find(d => d.value === selectedDate);
      if (currentDateOption?.disabled) {
        const firstAvailableDate = dateOptions.find(d => !d.disabled);
        if (firstAvailableDate) {
          setSelectedDate(firstAvailableDate.value);
        }
      }
    } else {
      // If no date is selected, select the first available date
      const firstAvailableDate = dateOptions.find(d => !d.disabled);
      if (firstAvailableDate) {
        setSelectedDate(firstAvailableDate.value);
      }
    }
  }, [currentTestTime, dateOptions]);

  // Update selected time when time options change due to date/time changes
  useEffect(() => {
    if (selectedDate && selectedTime) {
      const selectedDateObj = startOfDay(new Date(selectedDate));
      const hour = parseInt(selectedTime);
      const isCurrentTimeDisabled = isTimeDisabled(selectedDateObj, hour, currentTestTime);

      // If the currently selected time is now disabled, select the first available time
      if (isCurrentTimeDisabled) {
        const firstAvailableTime = timeOptions.find(t => !t.disabled);
        if (firstAvailableTime) {
          setSelectedTime(firstAvailableTime.value);
          updateCollectionDateTime(selectedDate, firstAvailableTime.value);
        }
      } else {
        updateCollectionDateTime(selectedDate, selectedTime);
      }
    }
  }, [selectedDate, selectedTime, currentTestTime]);
  const handleDateChange = (value: string) => {
    if (!value) return;

    // Find the date option
    const dateOption = dateOptions.find(d => d.value === value);
    if (dateOption?.disabled) return; // Don't allow selecting disabled dates

    setSelectedDate(value);

    // Update time options based on the new date
    if (value && selectedTime) {
      const selectedDateObj = startOfDay(new Date(value));
      const hour = parseInt(selectedTime);

      // If the current time is disabled with the new date, find first available time
      if (isTimeDisabled(selectedDateObj, hour, currentTestTime)) {
        const updatedTimeOptions = Array.from({
          length: 10
        }, (_, i) => {
          const timeHour = i + 9;
          return {
            value: timeHour.toString(),
            label: format(setHours(today, timeHour), 'h:mm a'),
            hour: timeHour,
            disabled: isTimeDisabled(selectedDateObj, timeHour, currentTestTime)
          };
        });
        const firstAvailableTime = updatedTimeOptions.find(t => !t.disabled);
        if (firstAvailableTime) {
          setSelectedTime(firstAvailableTime.value);
          updateCollectionDateTime(value, firstAvailableTime.value);
        }
      } else {
        updateCollectionDateTime(value, selectedTime);
      }
    }
  };
  const handleTimeChange = (value: string) => {
    if (!value) return;

    // Find the time option
    const timeOption = timeOptions.find(t => t.value === value);
    if (timeOption?.disabled) return; // Don't allow selecting disabled times

    setSelectedTime(value);
    if (selectedDate && value) {
      updateCollectionDateTime(selectedDate, value);
    }
  };
  return <div className="space-y-4">
      <div>
        <Label htmlFor="collection-date">Select a collection Date?</Label>
        <div className="mt-2">
          <ToggleGroup type="single" value={selectedDate} onValueChange={handleDateChange} className="flex flex-wrap gap-2">
            {dateOptions.map(date => <TooltipProvider key={date.value}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <ToggleGroupItem 
                        value={date.value} 
                        variant="outline" 
                        disabled={date.disabled} 
                        className={cn(
                          "rounded-full px-3 py-1 text-sm", 
                          date.disabled && "opacity-50 cursor-not-allowed",
                          date.isWeekend && !date.disabled && "border-orange-400 border-dashed",
                          selectedDate === date.value && !date.disabled && "bg-primary text-primary-foreground border-primary hover:bg-primary hover:text-primary-foreground"
                        )} 
                        style={selectedDate === date.value && !date.disabled ? {
                          borderColor: primaryColor,
                          backgroundColor: primaryColor,
                          color: '#ffffff'
                        } : date.isWeekend && !date.disabled ? {
                          borderColor: '#fb923c'
                        } : undefined}>
                        {date.disabled && <AlertCircle className="h-3 w-3 mr-1 inline text-orange-500" />}
                        {date.label}
                      </ToggleGroupItem>
                    </div>
                  </TooltipTrigger>
                  {date.disabled && <TooltipContent>
                      <p>{getDisabledReason(true, date.date, undefined, currentTestTime)}</p>
                    </TooltipContent>}
                </Tooltip>
              </TooltipProvider>)}
          </ToggleGroup>
          {selectedDate && (
            (() => {
              const selectedDateOption = dateOptions.find(d => d.value === selectedDate);
              if (selectedDateOption?.isWeekend) {
                return (
                  <div className="mt-2 text-sm text-center text-orange-600">
                    Collections placed for weekends or public holidays may attract a surcharge
                  </div>
                );
              }
              return null;
            })()
          )}
        </div>
      </div>
      
      <div>
        <Label htmlFor="collection-time">What time will the package be ready</Label>
        <div className="mt-2">
          <ToggleGroup type="single" value={selectedTime} onValueChange={handleTimeChange} className="flex flex-wrap gap-2">
            {timeOptions.map(time => {
            const timeDate = selectedDate ? startOfDay(new Date(selectedDate)) : today;
            const isDisabled = selectedDate ? isTimeDisabled(timeDate, time.hour, currentTestTime) : false;
            return <TooltipProvider key={time.value}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <ToggleGroupItem value={time.value} variant="outline" disabled={isDisabled} className={cn("rounded-full px-3 py-1 text-sm", isDisabled && "opacity-50 cursor-not-allowed", selectedTime === time.value && !isDisabled && "bg-primary text-primary-foreground border-primary hover:bg-primary hover:text-primary-foreground")} style={selectedTime === time.value && !isDisabled ? {
                      borderColor: primaryColor,
                      backgroundColor: primaryColor,
                      color: '#ffffff'
                    } : undefined}>
                          <Clock className="h-3 w-3 mr-1" />
                          {time.label}
                          {isDisabled && <AlertCircle className="h-3 w-3 ml-1 inline text-orange-500" />}
                        </ToggleGroupItem>
                      </div>
                    </TooltipTrigger>
                    {isDisabled && <TooltipContent>
                        <p>{getDisabledReason(false, timeDate, time.hour, currentTestTime)}</p>
                      </TooltipContent>}
                  </Tooltip>
                </TooltipProvider>;
          })}
          </ToggleGroup>
          {selectedTime && (
            <div className={cn(
              "mt-2 text-sm text-center",
              parseInt(selectedTime) < 17 
                ? "text-muted-foreground"
                : "text-destructive"
            )}>
              {parseInt(selectedTime) < 17 
                ? "Driver will arrive between the indicated time and COB or 17:00"
                : "Collections Ready after 17:00 may attract a surcharge"
              }
            </div>
          )}
        </div>
      </div>
    </div>;
};
export default CollectionDateTimeSelector;